import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug],
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="px-4 md:px-6 max-w-4xl mx-auto py-16 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
          <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
        <p className="text-foreground-500 mb-6">该文章不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '福利铺子',
    },
    url: `https://fulipuzi.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://fulipuzi.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <PageSEO
        title={`${article.title} - 福利铺子`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://fulipuzi.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaJsonLd={articleJsonLd}
      />
      <article className="px-4 md:px-6 max-w-4xl mx-auto py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categoryId}` },
            { label: article.title },
          ]}
        />

        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Link
              to={`/category/${article.categoryId}`}
              className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line"></i>
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.readTime}
            </span>
          </div>
        </header>

        <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="prose-custom mb-10"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="border-t border-background-200 pt-8">
          <h3 className="font-heading font-bold text-foreground-900 text-lg mb-5">
            相关文章推荐
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            {relatedArticles.map((related) => (
              <Link
                key={related.id}
                to={`/article/${related.slug}`}
                className="group block bg-white rounded-xl border border-background-200 hover:border-primary-200 hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src={related.coverImage}
                    alt={related.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {related.title}
                  </h4>
                  <p className="text-xs text-foreground-400 mt-1">{related.publishedAt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </>
  );
}